import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// project imports
import NavItem from '../NavItem';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
    const theme = useTheme();
    const customization = useSelector(state => state.customization);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const handleClick = () => {
        setOpen(!open);
        setSelected(!selected ? menu.id : null);
    };

    const { pathname } = useLocation();
    const checkOpenForParent = (child, id) => {
        child.forEach(item => {
            if (item.url === pathname) {
                setOpen(true);
                setSelected(id);
            }
        });
    };

    // menu collapse for sub-levels
    useEffect(() => {
        setOpen(false);
        setSelected(null);
        if (menu.children) {
            menu.children.forEach(item => {
                if (item.children?.length) {
                    checkOpenForParent(item.children, menu.id);
                }
                if (item.url === pathname) {
                    setSelected(menu.id);
                    setOpen(true);
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, menu.children]);

    // menu collapse & item
    const menus = menu.children?.map(item => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id} variant='h6' color='error' align='center'>
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = menu.icon ? (
        <Icon strokeWidth={1.5} size='1.3rem' style={{ marginTop: 'auto', marginBottom: 'auto' }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selected === menu.id ? 8 : 6,
                height: selected === menu.id ? 8 : 6,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    return (
        <>
            <ListItemButton
                sx={{
                    borderRadius: '12px',
                    mb: 1,
                    alignItems: 'center',
                    backgroundColor:
                        selected === menu.id
                            ? 'rgba(255,255,255,0.2)'
                            : level > 1
                            ? 'transparent !important'
                            : 'transparent',
                    py: level > 1 ? 1.25 : 1.5,
                    pl: `${level * 20 + 8}px`,
                    pr: 2,
                    color: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before':
                        selected === menu.id
                            ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: '4px',
                                  background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
                                  borderRadius: '0 4px 4px 0',
                                  boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                              }
                            : {},
                    '&:hover': {
                        backgroundColor: selected === menu.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
                        transform: 'translateX(4px)',
                        boxShadow: selected ? '0 6px 20px rgba(255,255,255,0.25)' : '0 4px 15px rgba(0,0,0,0.15)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 700,
                        boxShadow: '0 4px 15px rgba(255,255,255,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
                    },
                }}
                selected={selected === menu.id}
                onClick={handleClick}
            >
                <ListItemIcon
                    sx={{
                        my: 'auto',
                        minWidth: !menu.icon ? 18 : 40,
                        color: selected === menu.id ? '#FFD700' : 'rgba(255,255,255,0.9)',
                        transition: 'all 0.3s ease',
                    }}
                >
                    {menuIcon}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            variant={selected === menu.id ? 'body1' : 'body2'}
                            color='inherit'
                            sx={{
                                my: 'auto',
                                fontWeight: selected === menu.id ? 700 : 500,
                                fontSize: selected === menu.id ? '0.95rem' : '0.875rem',
                                letterSpacing: selected === menu.id ? '0.3px' : '0.2px',
                            }}
                        >
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography
                                variant='caption'
                                sx={{
                                    ...theme.typography.subMenuCaption,
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.75rem',
                                    mt: 0.5,
                                }}
                                display='block'
                                gutterBottom
                            >
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {open ? (
                    <IconChevronUp
                        stroke={2}
                        size='1.1rem'
                        style={{
                            marginTop: 'auto',
                            marginBottom: 'auto',
                            color: selected === menu.id ? '#FFD700' : 'rgba(255,255,255,0.8)',
                            transition: 'all 0.3s ease',
                        }}
                    />
                ) : (
                    <IconChevronDown
                        stroke={2}
                        size='1.1rem'
                        style={{
                            marginTop: 'auto',
                            marginBottom: 'auto',
                            color: selected === menu.id ? '#FFD700' : 'rgba(255,255,255,0.8)',
                            transition: 'all 0.3s ease',
                        }}
                    />
                )}
            </ListItemButton>
            <Collapse in={open} timeout='auto' unmountOnExit>
                <List
                    component='div'
                    disablePadding
                    sx={{
                        position: 'relative',
                        pl: 2,
                        '&:after': {
                            content: "''",
                            position: 'absolute',
                            left: '40px',
                            top: 0,
                            height: '100%',
                            width: '2px',
                            opacity: 0.6,
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                            borderRadius: '2px',
                        },
                    }}
                >
                    {menus}
                </List>
            </Collapse>
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number,
};

export default NavCollapse;
